import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { TextField } from '@mui/material';
import { Accordion } from '@design-system/components/accordion/Accordion';
import { AccordionHeader } from '../components/AccordionHeader';
import { sectionConfigs } from '../components/sectionConfigs';
import { PHONE_COUNTRY_OPTIONS } from '../../auth/model/auth.phone-options.model';
import { PhoneNumberInput } from '@design-system/components/input/phoneNumberInput';
import { useContactInfoViewModel } from './useContactInfo.viewModel';

type ContactInfoSectionProps = {
  influencerId: number;
  email: string | null | undefined;
  mobilePhoneNumber: string | null | undefined;
};

export const ContactInfoSection = ({
  influencerId,
  email,
  mobilePhoneNumber,
}: ContactInfoSectionProps) => {
  const vm = useContactInfoViewModel({
    influencerId,
    email,
    mobilePhoneNumber,
  });

  const config = sectionConfigs.contactInfo;

  const renderFooter = () => {
    if (vm.saveState === 'saved' && !vm.isDirty) {
      return (
        <div className="flex justify-end items-center gap-2 pt-4">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-green-600 text-sm"
          />
          <span className="text-sm text-green-600 font-medium">
            Changes saved
          </span>
        </div>
      );
    }

    if (vm.isDirty) {
      return (
        <div className="flex justify-end items-center gap-3 pt-4">
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-transparent border-none cursor-pointer"
            onClick={vm.onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-[#ede5fc] text-[#350584] text-sm font-medium px-4 py-2 rounded-[10px] border-none cursor-pointer disabled:opacity-50"
            onClick={vm.onSubmit}
            disabled={vm.saveState === 'saving'}
          >
            Save changes
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <Accordion
      defaultOpen={true}
      canClose={!vm.isDirty}
      renderHeader={() => (
        <AccordionHeader
          icon={<FontAwesomeIcon icon={faUser} />}
          iconBg={config.iconBg}
          iconColor={config.iconColor}
          title={config.title}
          description={config.description}
        />
      )}
      renderFooter={renderFooter}
    >
      <div className="flex flex-col gap-4">
        <div className="w-full">
          <span className="p-1 leading-5 text-sm font-medium">Email</span>
          <TextField
            className="w-full"
            placeholder="Your email"
            disabled={vm.saveState === 'saving'}
            {...vm.register('email')}
          />
          {vm.errors.email?.message ? (
            <span className="text-red-500 text-sm mt-1">
              {vm.errors.email.message}
            </span>
          ) : null}
        </div>
        <div className="w-full">
          <span className="p-1 leading-5 text-sm font-medium">
            Phone number
          </span>
          <PhoneNumberInput
            options={PHONE_COUNTRY_OPTIONS}
            selectedDialCode={vm.watch('phoneCountryDialCode') ?? ''}
            phoneNumber={vm.watch('phoneNumber') ?? ''}
            disabled={vm.saveState === 'saving'}
            onDialCodeChange={(value) =>
              vm.setValue('phoneCountryDialCode', value, { shouldDirty: true })
            }
            onPhoneNumberChange={(value) =>
              vm.setValue('phoneNumber', value, { shouldDirty: true })
            }
            testId="settings-phone-number"
          />
          {vm.errors.phoneNumber?.message ? (
            <span className="text-red-500 text-sm mt-1">
              {vm.errors.phoneNumber.message}
            </span>
          ) : null}
        </div>
        <p className="text-sm font-medium leading-snug text-[#646464] mt-0">
          This information does not pertain to logging data.
        </p>
      </div>
    </Accordion>
  );
};
